import '../assets/useImageStyles.css';
import { h, defineComponent, ref, watchEffect, onMounted, onBeforeUnmount, openBlock, createElementBlock, createBlock, unref, createCommentVNode, computed } from 'vue';
import { u as useI18n } from './widgets-core.es.C7uAbUpp.js';
import { o as useStore } from './useStore.B0k3KXLh.js';
import { g as getButtonActionType } from './Text.vue_vue_type_style_index_0_lang.CFVGfjC7.js';
import { D as DeviceKind } from './maps.nxkzRTum.js';

const icon = (props, context) => {
    const xmlns = "http://www.w3.org/2000/svg";
    const svgCode = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
  <path fill-rule="evenodd" d="M5 3.25A1.75 1.75 0 003.25 5v6.523l2.886-2.887.01-.01c.5-.48 1.11-.767 1.77-.767.66 0 1.271.287 1.77.767l.011.01 1.97 1.97.303-.303.01-.01c.5-.48 1.11-.768 1.77-.768.66 0 1.27.287 1.77.768l.01.01 1.22 1.22V5A1.75 1.75 0 0015 3.25H5zm11.75 10.394l-2.275-2.275c-.272-.26-.523-.344-.725-.344-.202 0-.453.084-.725.344l-.298.298 1.137 1.136a.75.75 0 01-1.061 1.06l-1.666-1.665-.001-.002-2.494-2.493c-.272-.26-.523-.344-.725-.344-.202 0-.453.083-.726.344L3.25 13.644V15A1.75 1.75 0 005 16.75h10A1.75 1.75 0 0016.75 15v-1.356zM1.75 15A3.25 3.25 0 005 18.25h10A3.25 3.25 0 0018.25 15V5A3.25 3.25 0 0015 1.75H5A3.25 3.25 0 001.75 5v10zm10-8.333a.75.75 0 01.75-.75h.008a.75.75 0 010 1.5H12.5a.75.75 0 01-.75-.75z" clip-rule="evenodd"/>
</svg>
`;
    
    const svgRoot = new DOMParser().parseFromString(svgCode, 'image/svg+xml').firstChild;
    
    if (props.title) {
      const title = document.createElementNS(xmlns, 'title');
      title.textContent = props.title;
      svgRoot.appendChild(title);
    }

    const svgAttributes = Array
      .from(svgRoot?.attributes ?? [])
      .reduce((acc, curr) => ({
        ...acc,
        [curr.name]: curr.value
      }));

    return h(
      'svg',
      {
        ...svgAttributes,
        ...context.attrs,
        role: 'img',
        innerHTML: svgRoot.innerHTML
      },
    );
  };

const _hoisted_1 = ["data-module-id", "data-no-image"];
const _hoisted_2 = ["data-image-loaded", "src", "alt"];
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ImageWithBehaviour",
  props: {
    moduleProps: {},
    maxWidth: {},
    maxHeight: {}
  },
  emits: ["naturalImageHeight", "imageWidth", "imageLoad"],
  setup(__props, { emit: __emit }) {
    const { t } = useI18n();
    const props = __props;
    const imageDom = ref(null);
    const imageUrl = ref();
    const imageAlt = ref();
    const imageWidth = ref(props.maxWidth);
    const imageHeight = ref(props.maxHeight);
    const imageLoaded = ref(false);
    const imagePreloading = ref(false);
    const imageLoadedAspectRatio = ref(1);
    const store = useStore();
    const emit = __emit;
    function emitImageWidth() {
      if (imageDom.value) {
        emit("imageWidth", imageDom.value.offsetWidth);
      }
    }
    function onImageLoaded() {
      if (imageDom.value) {
        const {
          naturalWidth,
          naturalHeight
        } = imageDom.value;
        imageLoadedAspectRatio.value = naturalWidth / naturalHeight;
        imageLoaded.value = true;
        emit("imageLoad", imageLoaded.value);
        emit("naturalImageHeight", naturalHeight);
        emitImageWidth();
      }
    }
    function preloadImage(newImageUrl) {
      imageLoaded.value = false;
      emit("imageLoad", imageLoaded.value);
      imagePreloading.value = true;
      const newImage = new Image();
      newImage.onload = () => {
        imageUrl.value = newImageUrl;
        newImage.remove();
      };
      newImage.onerror = () => {
        imageUrl.value = null;
        newImage.remove();
      };
      newImage.src = newImageUrl;
    }
    watchEffect(() => {
      if (props.moduleProps.image) {
        const newImageUrl = store?.literals.getLocaleImageUrl(props.moduleProps.image);
        if (newImageUrl && newImageUrl !== imageUrl.value) {
          preloadImage(newImageUrl);
        }
        imageAlt.value = store?.literals.getLocaleImageUrl(props.moduleProps.image);
      }
    });
    watchEffect(() => {
      const maxImageWidth = props.moduleProps.maxWidth ? Math.min(props.moduleProps.maxWidth, props.maxWidth) : props.maxWidth;
      imageHeight.value = Math.min(props.maxHeight, maxImageWidth / imageLoadedAspectRatio.value);
      imageWidth.value = imageHeight.value * imageLoadedAspectRatio.value;
    });
    function onClickImage() {
      const buttonActionType = getButtonActionType(props.moduleProps.behaviour.type);
      store?.handlers.callHandler(buttonActionType, props.moduleProps.id);
    }
    onMounted(() => {
      window.addEventListener("resize", emitImageWidth);
    });
    onBeforeUnmount(() => {
      window.removeEventListener("resize", emitImageWidth);
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        class: "thn--tool-image-with-behaviour",
        "data-module-id": props.moduleProps.id,
        "data-no-image": !imageUrl.value && !imagePreloading.value,
        onClick: onClickImage
      }, [
        !imageUrl.value && !imagePreloading.value ? (openBlock(), createBlock(unref(icon), {
          key: 0,
          class: "thn--tool-image-with-behaviour__icon",
          alt: unref(t)("Picture icon")
        }, null, 8, ["alt"])) : createCommentVNode("", true),
        imageUrl.value ? (openBlock(), createElementBlock("img", {
          key: 1,
          class: "thn--tool-image-with-behaviour__image",
          ref_key: "imageDom",
          ref: imageDom,
          "data-image-loaded": imageLoaded.value,
          src: imageUrl.value,
          alt: imageAlt.value,
          onLoad: onImageLoaded
        }, null, 40, _hoisted_2)) : createCommentVNode("", true)
      ], 8, _hoisted_1);
    };
  }
});

function useImageAttrs(layout, device) {
  const store = useStore();
  const imageDesktopUrl = computed(() => layout.value?.image ? store?.literals.getLocaleImageUrl(layout.value.image) : "");
  const imageMobileUrl = computed(() => layout.value?.imageMobile ? store?.literals.getLocaleImageUrl(layout.value.imageMobile) : "");
  const imageDesktopAlt = computed(() => layout.value?.image ? store?.literals.getLocaleImageAlt(layout.value.image) : "");
  const imageMobileAlt = computed(() => layout.value?.imageMobile ? store?.literals.getLocaleImageAlt(layout.value.imageMobile) : "");
  const imageUrl = computed(() => {
    if (layout.value.sync === false) {
      return device.value === DeviceKind.Mobile ? imageMobileUrl.value : imageDesktopUrl.value;
    }
    return device.value === DeviceKind.Mobile && imageMobileUrl.value ? imageMobileUrl.value : imageDesktopUrl.value;
  });
  const imageAlt = computed(() => {
    if (layout.value.sync === false) {
      return device.value === DeviceKind.Mobile ? imageMobileAlt.value : imageDesktopAlt.value;
    }
    return device.value === DeviceKind.Mobile && imageMobileUrl.value ? imageMobileAlt.value : imageDesktopAlt.value;
  });
  return {
    imageUrl,
    imageAlt
  };
}

const useImageStyles = (layout, device, imagePosition = ref("left")) => {
  const imageStyles = computed(() => {
    if (device.value === DeviceKind.Desktop && !layout.value?.imagePadding) return "";
    if (device.value === DeviceKind.Mobile && !layout.value?.imageMobilePadding) return "";
    const layoutPadding = device.value === DeviceKind.Mobile ? layout.value.imageMobilePadding : layout.value.imagePadding;
    if (!layoutPadding) return "";
    const {
      top,
      bottom,
      left,
      right,
      format
    } = layoutPadding;
    const imagePaddings = {
      top: `${top}${format}`,
      bottom: imagePosition.value !== "top" ? `${bottom}${format}` : "0px",
      right: imagePosition.value !== "left" ? `${right}${format}` : "0px",
      left: imagePosition.value !== "right" ? `${left}${format}` : "0px"
    };
    return `
      padding: ${imagePaddings.top} ${imagePaddings.right} ${imagePaddings.bottom} ${imagePaddings.left};
    `;
  });
  return {
    imageStyles
  };
};

export { _sfc_main as _, useImageStyles as a, icon as i, useImageAttrs as u };
//# sourceMappingURL=useImageStyles.BEVtIsp6.js.map
